import { Timestamp } from 'firebase/firestore';

/**
 * 파일 카테고리 타입
 * Phase 1: Firebase Storage에 모든 타입 저장
 * Phase 2: CAD/3D는 NAS로 마이그레이션
 */
export type FileCategory = 
  | '설계도면'      // CAD 파일 (DWG, DXF)
  | '3D모델'        // STEP, IGES, STP, X_T, PRT
  | '문서'          // 사출기준서, 검사성적서 (XLSX, PDF, DOCX)
  | '사진'          // 금형/보관장소 사진 (JPG, PNG)
  | 'CT결과서'      // CT 검사 결과
  | 'QR코드'        // QR 코드 이미지
  | '기타';         // 기타 파일

/**
 * 저장소 타입
 * Phase 1: Firebase만 사용
 * Phase 2: NAS 추가
 */
export type StorageType = 'firebase' | 'nas';

/**
 * 프로젝트 파일 인터페이스
 * projects/{projectId}/files 서브컬렉션
 */
export interface ProjectFile {
  id?: string;                    // Firestore Document ID
  fileName: string;               // 원본 파일명
  fileType: string;               // 확장자 (소문자)
  category: FileCategory;         // 자동 분류된 카테고리
  
  // 저장 위치 (듀얼 스토리지)
  storageType: StorageType;       // 'firebase' | 'nas'
  masterStorage: 'firebase' | 'nas'; // 메인 저장소 (듀얼 시 NAS)
  storagePath: string;            // Storage 경로
  fileUrl?: string;               // Firebase Storage URL (firebase만)
  nasPath?: string;               // NAS 전체 경로 (/egmfs_files/projects/...)
  firebasePath?: string;          // Firebase 캐시 경로 (하이브리드 모드)
  humanReadablePath: string;      // 사람이 읽기 쉬운 경로 (projects/P-2024-001/drawings/file.dwg)
  
  // 파일 정보
  fileSize: number;               // bytes
  mimeType: string;               // MIME type
  
  // 썸네일 (이미지/PDF만)
  thumbnailUrl?: string;          // 썸네일 URL
  hasThumbnail: boolean;          // 썸네일 존재 여부
  
  // 메타데이터
  uploadedAt: Timestamp;
  uploadedBy: string;             // userId
  uploadedByName?: string;        // 업로더 이름 (표시용)
  
  // 검색 및 추가 정보
  searchTerms: string[];          // [fileName, category, tags...]
  tags?: string[];                // 사용자 정의 태그
  description?: string;           // 파일 설명
  version?: string;               // 버전 번호 (v1.0)
  isLatest: boolean;              // 최신 버전 여부
  
  // 메타 정보
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

/**
 * 금형 파일 인터페이스
 * molds/{moldId}/files 서브컬렉션
 * ProjectFile과 동일한 구조
 */
export type MoldFile = ProjectFile;

/**
 * 파일 업로드 폼 데이터
 */
export interface FileUploadData {
  file: File;                     // 실제 파일 객체
  category?: FileCategory;        // 수동 카테고리 선택 (선택사항)
  description?: string;           // 파일 설명
  tags?: string[];                // 태그
  version?: string;               // 버전
}

/**
 * 파일 다운로드 정보
 */
export interface FileDownloadInfo {
  fileName: string;
  fileUrl: string;
  fileSize: number;
}

/**
 * 파일 타입별 카테고리 매핑
 */
export const FILE_CATEGORY_MAP: Record<string, FileCategory> = {
  // 설계도면
  'dwg': '설계도면',
  'dxf': '설계도면',
  
  // 3D 모델
  'stp': '3D모델',
  'step': '3D모델',
  'igs': '3D모델',
  'iges': '3D모델',
  'x_t': '3D모델',
  'prt': '3D모델',
  
  // 문서
  'xlsx': '문서',
  'xls': '문서',
  'pdf': '문서',
  'docx': '문서',
  'doc': '문서',
  'pptx': '문서',
  'ppt': '문서',
  'hwp': '문서',
  'hwpx': '문서',
  'txt': '문서',
  
  // 사진
  'jpg': '사진',
  'jpeg': '사진',
  'png': '사진',
  'gif': '사진',
  'webp': '사진',
  'bmp': '사진',
  
  // 기타
  'zip': '기타',
  'rar': '기타',
  '7z': '기타',
  
  // 오디오
  'mp3': '기타',
  'wav': '기타',
  'aac': '기타',
  'flac': '기타',
  'ogg': '기타',
  
  // 비디오
  'mp4': '기타',
  'avi': '기타',
  'mov': '기타',
  'wmv': '기타',
  'mkv': '기타',
  'webm': '기타',
};

/**
 * 카테고리별 색상 (UI용)
 */
export const CATEGORY_COLORS: Record<FileCategory, string> = {
  '설계도면': '#1976d2',    // 파랑
  '3D모델': '#9c27b0',      // 보라
  '문서': '#2e7d32',        // 초록
  '사진': '#ed6c02',        // 주황
  'CT결과서': '#d32f2f',    // 빨강
  'QR코드': '#0288d1',      // 하늘
  '기타': '#757575',        // 회색
};

/**
 * 카테고리별 아이콘 (Material Symbols)
 */
export const CATEGORY_ICONS: Record<FileCategory, string> = {
  '설계도면': 'architecture',
  '3D모델': 'view_in_ar',
  '문서': 'description',
  '사진': 'image',
  'CT결과서': 'science',
  'QR코드': 'qr_code_2',
  '기타': 'folder',
};

/**
 * 파일 크기 제한 (500MB)
 */
export const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500MB in bytes

/**
 * Firebase 캐시 임계값 (10MB)
 * 이 크기 이하의 파일은 NAS + Firebase 듀얼 저장
 */
export const FIREBASE_CACHE_THRESHOLD = 10 * 1024 * 1024; // 10MB in bytes

/**
 * 파일 확장자로 카테고리 자동 결정
 */
export function getFileCategoryFromExtension(fileName: string): FileCategory {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  return FILE_CATEGORY_MAP[ext] || '기타';
}

/**
 * 파일 크기를 읽기 쉬운 형식으로 변환
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}

/**
 * 썸네일 생성 가능 여부 확인
 */
export function canGenerateThumbnail(fileType: string): boolean {
  const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];
  const pdfTypes = ['pdf'];
  
  return imageTypes.includes(fileType) || pdfTypes.includes(fileType);
}

/**
 * 허용된 파일 타입 (Phase 1)
 */
export const ALLOWED_FILE_TYPES = Object.keys(FILE_CATEGORY_MAP);

/**
 * MIME Type 매핑
 */
export const MIME_TYPE_MAP: Record<string, string> = {
  // 설계도면
  'dwg': 'application/acad',
  'dxf': 'application/dxf',
  
  // 3D 모델
  'stp': 'application/step',
  'step': 'application/step',
  'igs': 'application/iges',
  'iges': 'application/iges',
  'x_t': 'application/x_parasolid',
  'prt': 'application/x-prt',
  
  // 문서
  'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'xls': 'application/vnd.ms-excel',
  'pdf': 'application/pdf',
  'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'doc': 'application/msword',
  'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'ppt': 'application/vnd.ms-powerpoint',
  'hwp': 'application/x-hwp',
  'hwpx': 'application/x-hwpx',
  'txt': 'text/plain',
  
  // 사진
  'jpg': 'image/jpeg',
  'jpeg': 'image/jpeg',
  'png': 'image/png',
  'gif': 'image/gif',
  'webp': 'image/webp',
  'bmp': 'image/bmp',
  
  // 기타
  'zip': 'application/zip',
  'rar': 'application/x-rar-compressed',
  '7z': 'application/x-7z-compressed',
  
  // 오디오
  'mp3': 'audio/mpeg',
  'wav': 'audio/wav',
  'aac': 'audio/aac',
  'flac': 'audio/flac',
  'ogg': 'audio/ogg',
  
  // 비디오
  'mp4': 'video/mp4',
  'avi': 'video/x-msvideo',
  'mov': 'video/quicktime',
  'wmv': 'video/x-ms-wmv',
  'mkv': 'video/x-matroska',
  'webm': 'video/webm',
};


/**
 * Phase 1: 파일 관리 UI 강화를 위한 확장 타입
 * 기존 ProjectFile에 검색, 통계, 즐겨찾기 기능 추가
 */
export interface EnhancedProjectFile extends ProjectFile {
  // 검색 최적화
  searchKeywords: string[];       // 자동 생성 검색 키워드
  fileExtension: string;          // 확장자 (소문자)
  
  // 사용 통계
  viewCount: number;              // 조회 수
  downloadCount: number;          // 다운로드 수
  lastAccessedAt?: Timestamp;     // 마지막 접근 시간
  
  // 즐겨찾기
  isFavorite: boolean;            // 현재 사용자 즐겨찾기 여부 (클라이언트 계산)
  favoritedBy: string[];          // 즐겨찾기한 사용자 ID 목록
  
  // 중요도
  importance: 'low' | 'medium' | 'high';
}

/**
 * 업로더 정보 인터페이스
 */
export interface UploaderInfo {
  id: string;           // 사용자 ID
  name: string;         // 사용자 이름
  email: string;        // 사용자 이메일
}

/**
 * 파일 필터 인터페이스
 */
export interface FileFilters {
  category: FileCategory | '전체';
  uploader: string | null;
  dateRange: {
    start: Date;
    end: Date;
  } | null;
  sizeRange: {
    min: number; // bytes
    max: number;
  } | null;
  tags: string[];
  onlyFavorites: boolean;
}

/**
 * 기본 필터 값 반환
 */
export const getDefaultFilters = (): FileFilters => ({
  category: '전체',
  uploader: null,
  dateRange: null,
  sizeRange: null,
  tags: [],
  onlyFavorites: false,
});
